// src/pages/ProfilePage.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import ActivityCard from '../components/ActivityCard';
import { useAuthContext } from '../contexts/AuthContext';
import config from '../config';
import './ProfilePage.css';
import './DashboardPage.css'; // For feed styles

export default function ProfilePage() {
    const { userId } = useParams();
    const { user, API_URL } = useAuthContext();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isOwnProfile = !userId || (user && userId === String(user.id));

    useEffect(() => {
        document.title = `Profile | ${config.siteTitle}`;
    }, []);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoading(true);
                const endpoint = isOwnProfile
                    ? `${API_URL}/api/users/me/profile`
                    : `${API_URL}/api/users/${userId}/profile`;

                const response = await fetch(endpoint, {
                    credentials: 'include',
                });

                if (response.ok) {
                    const data = await response.json();
                    setProfile(data.profile);
                } else if (response.status === 404) {
                    setError('User not found');
                } else {
                    setError('Failed to load profile');
                }
            } catch (err) {
                console.error('Failed to fetch profile:', err);
                setError('Failed to load profile');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [API_URL, userId, isOwnProfile]);

    const getRankClassName = (rankLabel) => {
        switch (rankLabel) {
            case 'Kipchoge': return 'rank-kipchoge';
            case 'Pro': return 'rank-pro';
            case 'Serious Runner': return 'rank-serious';
            default: return 'rank-hobby';
        }
    };

    const formatChartDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return (
        <div className="profile-page">
            <Nav />

            <main className="profile-content">
                {loading ? (
                    <div className="loading">Loading profile...</div>
                ) : error ? (
                    <div className="error-state">
                        <p>{error}</p>
                    </div>
                ) : profile ? (
                    <>
                        {/* Profile Header */}
                        <div className="profile-header">
                            <div className="profile-avatar">
                                {profile.username?.[0]?.toUpperCase() || '?'}
                            </div>
                            <div className="profile-info">
                                <h1 className="profile-username">
                                    {profile.username}
                                    {isOwnProfile && <span className="you-badge">You</span>}
                                </h1>
                                {profile.location && (
                                    <p className="profile-location">{profile.location}</p>
                                )}
                            </div>
                        </div>

                        {/* Stats Cards */}
                        <div className="profile-stats">
                            <div className="stat-card stat-card-primary">
                                <span className="stat-value">
                                    {profile.average_serious_score ?? '—'}
                                </span>
                                <span className="stat-label">Serious Score</span>
                                {profile.rank_label && (
                                    <span className={`rank-badge ${getRankClassName(profile.rank_label)}`}>
                                        {profile.rank_label}
                                    </span>
                                )}
                            </div>
                            <div className="stat-card">
                                <span className="stat-value">{profile.total_activities}</span>
                                <span className="stat-label">Activities</span>
                            </div>
                        </div>

                        {/* Score History Chart */}
                        {profile.score_history && profile.score_history.length > 1 && (
                            <div className="chart-section">
                                <h2 className="section-title">Serious Score History</h2>
                                <div className="chart-container">
                                    <ResponsiveContainer width="100%" height={300}>
                                        <LineChart
                                            data={profile.score_history}
                                            margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                                            <XAxis
                                                dataKey="date"
                                                tickFormatter={formatChartDate}
                                                stroke="#666"
                                                fontSize={12}
                                            />
                                            <YAxis
                                                domain={[0, 100]}
                                                stroke="#666"
                                                fontSize={12}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: '#1a1a1a',
                                                    border: '1px solid #2a2a2a',
                                                    borderRadius: '4px',
                                                    color: '#fafafa'
                                                }}
                                                labelFormatter={formatChartDate}
                                                formatter={(value, name) => [value, 'Score']}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="score"
                                                stroke="#ff4d00"
                                                strokeWidth={2}
                                                dot={{ fill: '#ff4d00', strokeWidth: 0, r: 4 }}
                                                activeDot={{ r: 6, fill: '#ff6a2e' }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )}

                        {/* Empty chart state */}
                        {(!profile.score_history || profile.score_history.length <= 1) && (
                            <div className="empty-chart">
                                <p>Not enough activities to show score history.</p>
                            </div>
                        )}

                        {/* Activities Feed */}
                        {profile.activities && profile.activities.length > 0 && (
                            <div className="activities-section">
                                <h2 className="section-title">Activities</h2>
                                <div className="feed-list">
                                    {profile.activities.map((activity) => (
                                        <ActivityCard key={activity.id} activity={activity} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </main>

            <Footer />
        </div>
    );
}
